import { useState } from 'react';
import {
  Box, Typography, Button, Chip, Stack, Divider,
  FormControl, InputLabel, Select, MenuItem, ToggleButton, ToggleButtonGroup,
  Tab, Tabs,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import intl from 'components/i18n/ReactIntlWrapper';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { handleAPIError } from 'common/errorHandler';
import { HRApiWrapper } from 'api/HRApiWrapper';
import JobOpeningDrawer from './JobOpeningDrawer';
import ResizableDrawer from 'components/Layout/ResizableDrawer';

interface Props {
  open: boolean;
  onClose: () => void;
  onReload: () => void;
  jobOpening: any;
  hrApi: HRApiWrapper;
  skills: any[];
  seniorities: any[];
  educationLevels: any[];
}

const richTextSx = {
  fontSize: '0.875rem', lineHeight: 1.6,
  '& p': { mt: 0, mb: 0.5 },
  '& p:last-child': { mb: 0 },
  '& ul, & ol': { pl: 2.5, my: 0.5 },
  '& strong': { fontWeight: 600 },
};

const JobOpeningDetail: React.FC<Props> = ({
  open, onClose, onReload, jobOpening, hrApi, skills, seniorities, educationLevels,
}) => {
  const toast = useToastMessageQueue();
  const [tab, setTab] = useState(0);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedSkillId, setSelectedSkillId] = useState<number | ''>('');
  const [skillRequired, setSkillRequired] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors] = useState({});

  const linkedSkillIds = new Set((jobOpening.jobOpeningSkills ?? []).map((s: any) => s.idSkill ?? s.skill?.id));
  const availableSkills = skills.filter((s) => !linkedSkillIds.has(s.id));

  const handleAddSkill = async () => {
    if (!selectedSkillId) return;
    setSaving(true);
    try {
      const skill = skills.find((s) => s.id === Number(selectedSkillId));
      await hrApi.addSkillToJobOpening('1', jobOpening.id, {
        idSkill: Number(selectedSkillId),
        required: skillRequired,
        skillType: skill?.isSoft ? 'personal' : 'technical',
      });
      setSelectedSkillId('');
      onReload();
    } catch (error) {
      handleAPIError(error, toast, errors);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ResizableDrawer open={open} onClose={onClose} defaultWidth={720}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
          <Box>
            <Typography variant="h6">{jobOpening.title}</Typography>
            <Typography variant="body2" color="text.secondary">
              {jobOpening.seniority?.name}{jobOpening.requiredExperience ? ` · ${jobOpening.requiredExperience}y exp` : ''}
            </Typography>
          </Box>
          <Button size="small" onClick={() => setEditOpen(true)}>{intl.get('common.edit')}</Button>
        </Box>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label={intl.get('hr.jobOpening.tab.details')} />
          <Tab label={intl.get('hr.jobOpening.tab.skills')} />
        </Tabs>

        {tab === 0 && (
          <Box>
            {jobOpening.projectDescription && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>{intl.get('hr.jobOpening.projectDescription')}</Typography>
                <Box sx={richTextSx} dangerouslySetInnerHTML={{ __html: jobOpening.projectDescription }} />
              </Box>
            )}
            {jobOpening.roleDescription && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>{intl.get('hr.jobOpening.roleDescription')}</Typography>
                <Box sx={richTextSx} dangerouslySetInnerHTML={{ __html: jobOpening.roleDescription }} />
              </Box>
            )}
            {jobOpening.remarks && (
              <Box mb={2}>
                <Typography variant="subtitle2" gutterBottom>{intl.get('hr.jobOpening.remarks')}</Typography>
                <Box sx={richTextSx} dangerouslySetInnerHTML={{ __html: jobOpening.remarks }} />
              </Box>
            )}
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {(jobOpening.jobOpeningSkills ?? []).map((s: any) => (
                <Chip
                  key={s.id}
                  label={`${s.skill?.name} · ${s.skillType}`}
                  size="small"
                  color={s.required ? 'primary' : 'default'}
                  variant={s.required ? 'filled' : 'outlined'}
                  onDelete={() => hrApi.removeSkillFromJobOpening('1', jobOpening.id, s.id).then(onReload)}
                  deleteIcon={<DeleteIcon />}
                />
              ))}
              {(jobOpening.jobOpeningSkills ?? []).length === 0 && (
                <Typography variant="body2" color="text.secondary">{intl.get('hr.jobOpening.noSkills')}</Typography>
              )}
            </Box>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" gap={1}>
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>{intl.get('hr.skill.name')}</InputLabel>
                <Select value={selectedSkillId} onChange={(e) => setSelectedSkillId(e.target.value as number)} label={intl.get('hr.skill.name')}>
                  {availableSkills.map((s) => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                </Select>
              </FormControl>
              <ToggleButtonGroup value={skillRequired ? 'required' : 'desired'} exclusive size="small"
                onChange={(_, v) => v && setSkillRequired(v === 'required')}>
                <ToggleButton value="required">{intl.get('hr.jobOpening.required')}</ToggleButton>
                <ToggleButton value="desired">{intl.get('hr.jobOpening.desired')}</ToggleButton>
              </ToggleButtonGroup>
              <Button variant="outlined" disabled={!selectedSkillId || saving} onClick={handleAddSkill}>
                {intl.get('common.add')}
              </Button>
            </Stack>
          </Box>
        )}


      </ResizableDrawer>

      <JobOpeningDrawer
        open={editOpen}
        onClose={() => setEditOpen(false)}
        onSave={async (data) => { await hrApi.updateJobOpening('1', { ...data, id: jobOpening.id }); setEditOpen(false); onClose(); }}
        item={jobOpening}
        seniorities={seniorities}
        educationLevels={educationLevels}
      />
    </>
  );
};

export default JobOpeningDetail;
